import { useEffect, useState } from 'react';
import PortfolioList from './PortfolioList';
import PortfolioDetail from './PortfolioDetail';
import type { PortfolioEditState, CreditsafePortfolioRule } from './portfolioTypes';
import AddPortfolioModal from './AddPortfolioModal';
import { useAppDispatch, useAppSelector } from '../../../../redux/store';
import {
  deleteSavedMonitoringPortfolio,
  getSavedCreditSafeCompanies,
  getSavedMonitoringPortfolios,
  updateSavedMonitoringPortfolio,
} from '../../../../redux/services/creditReportService';
import { CreditSafePortfolio } from '@dill/dill-shared';
import { openDillToast } from '../../../../utils/helpers';

type View = 'list' | 'detail';

// ─────────────────────────────────────────────
// CONNECTOR
// Owns all state. No UI — purely a routing + state layer.
// Drop <PortfolioConnector /> anywhere to get the full feature.
// ─────────────────────────────────────────────

export default function PortfolioConnector() {
  const dispatch = useAppDispatch();
  // ── Core data ────────────────────────────────────────────────────────────
  const [portfolios, setPortfolios] = useState<CreditSafePortfolio[]>([]);

  // ── Navigation ───────────────────────────────────────────────────────────
  const [view, setView] = useState<View>('list');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [openPortfolioModal, setOpenPortfolioModal] = useState<boolean>(false);

  const { user } = useAppSelector((state) => state.auth);
  const currentSupplier = useAppSelector((state) => state.auth.currentSupplier);
  const { creditMonitoringPortfolios } = useAppSelector((state) => state.creditSafe);

  const [portfolioRules, setPortfolioRules] = useState<Record<string, CreditsafePortfolioRule[]>>(
    {}
  );

  useEffect(() => {
    dispatch(getSavedMonitoringPortfolios({ supplierId: currentSupplier?.id ?? '' }));
    dispatch(getSavedCreditSafeCompanies({ supplierId: currentSupplier?.id ?? '' }));
  }, [currentSupplier?.id]);

  useEffect(() => {
    setPortfolios(creditMonitoringPortfolios);
  }, [creditMonitoringPortfolios]);

  useEffect(() => {
    setPortfolioRules(
      Object.fromEntries(
        creditMonitoringPortfolios.map((p) => [p.id, (p.rules ?? []).map((r) => ({ ...r }))])
      )
    );
  }, [creditMonitoringPortfolios]);

  // ─────────────────────────────────────────────
  // NAVIGATION
  // ─────────────────────────────────────────────

  const handleSelect = (id: string) => {
    setSelectedId(id);
    setView('detail');
  };

  const handleBack = () => {
    setView('list');
    setSelectedId(null);
  };

  // ─────────────────────────────────────────────
  // PORTFOLIO CRUD
  // ─────────────────────────────────────────────

  const handleSavePortfolio = async (id: string, updated: PortfolioEditState) => {
    const updatedPortfolios = portfolios.map((p) => {
      if (p.id === id) return { ...p, ...updated, updatedAt: new Date() };
      // Clear default on others if this one is being set as default
      if (updated.isDefault) return { ...p, isDefault: false };
      return p;
    });
    setPortfolios(updatedPortfolios);

    const results = await dispatch(
      updateSavedMonitoringPortfolio({
        supplierId: currentSupplier?.id ?? '',
        updatedDetails: updated,
        portfolioId: id,
      })
    );
    if (results.type.includes('fulfilled')) {
      await dispatch(getSavedMonitoringPortfolios({ supplierId: currentSupplier?.id ?? '' }));
      openDillToast({ message: 'Successfully updated portfolio', type: 'SUCCESS' });
    }
  };

  const handleRemovePortfolio = async (id: string) => {
    if (id) {
      setView('list');
      setSelectedId(null);

      const deletedResult = await dispatch(
        deleteSavedMonitoringPortfolio({
          supplierId: currentSupplier?.id ?? '',
          portfolioId: id,
        })
      );
      if (deletedResult.type.includes('fulfilled')) {
        await dispatch(getSavedMonitoringPortfolios({ supplierId: currentSupplier?.id ?? '' }));

        setPortfolios((prev) => prev.filter((p) => p.id !== id));
        setPortfolioRules((prev) => {
          const n = { ...prev };
          delete n[id];
          return n;
        });
        openDillToast({ message: 'Successfully removed portfolio', type: 'SUCCESS' });
      }
    }
  };

  const handleNewPortfolio = () => {
    setOpenPortfolioModal(true);
  };

  // ─────────────────────────────────────────────
  // RULES
  // ─────────────────────────────────────────────

  /**
   * Called by PortfolioDetail whenever rules change (toggle, edit params,
   * delete, or modal save). Syncs back into portfolios[] so the list card
   * shows accurate rule counts and category pills.
   */
  const handleRulesChange = async (portfolioId: string, rules: CreditsafePortfolioRule[]) => {
    setPortfolioRules((prev) => ({ ...prev, [portfolioId]: rules }));
    setPortfolios((prev) => prev.map((p) => (p.id === portfolioId ? { ...p, rules } : p)));

    const results = await dispatch(
      updateSavedMonitoringPortfolio({
        supplierId: currentSupplier?.id ?? '',
        updatedDetails: {
          rules,
        },
        portfolioId: portfolioId,
      })
    );
    if (results.type.includes('fulfilled')) {
      await dispatch(getSavedMonitoringPortfolios({ supplierId: currentSupplier?.id ?? '' }));
      openDillToast({ message: 'Successfully updated rules', type: 'SUCCESS' });
    } else {
      openDillToast({ message: 'Failed to update rules', type: 'ERROR' });
    }
  };

  const handleEditCompany = async (
    portfolioId: string,
    company: { creditSafeId: string; name: string }[]
  ) => {
    const results = await dispatch(
      updateSavedMonitoringPortfolio({
        supplierId: currentSupplier?.id ?? '',
        updatedDetails: {
          companies: company,
        },
        portfolioId: portfolioId,
      })
    );
    if (results.type.includes('fulfilled')) {
      await dispatch(getSavedMonitoringPortfolios({ supplierId: currentSupplier?.id ?? '' }));
      openDillToast({ message: 'Successfully updated companies', type: 'SUCCESS' });
    } else {
      openDillToast({ message: 'Failed to update companies', type: 'ERROR' });
    }
  };

  // ─────────────────────────────────────────────
  // RENDER
  // ─────────────────────────────────────────────

  const selectedPortfolio = portfolios.find((p) => p.id === selectedId) ?? null;
  if (view === 'detail' && selectedPortfolio) {
    return (
      <div>
        <PortfolioDetail
          portfolio={selectedPortfolio}
          rules={portfolioRules[selectedPortfolio.id] ?? []}
          onBack={handleBack}
          onSavePortfolio={(updated) => handleSavePortfolio(selectedPortfolio.id, updated)}
          onRulesChange={(rules) => handleRulesChange(selectedPortfolio.id, rules)}
          onEditCompany={(company) => handleEditCompany(selectedPortfolio.id, company)}
        />
      </div>
    );
  }

  return (
    <div>
      <AddPortfolioModal
        open={openPortfolioModal}
        handleClose={() => setOpenPortfolioModal(false)}
      />

      <PortfolioList
        portfolios={portfolios}
        onSelect={handleSelect}
        onRemove={handleRemovePortfolio}
        onNewPortfolio={handleNewPortfolio}
      />
    </div>
  );
}
